// The invite panel's mode switch (components/InviteDropdown.js): 'email' or
// 'link'. Called by AppSegmented as `el.call('inviteDropdownMode', value)`,
// so `this` is the segment that was picked; its track (InviteMode) owns no
// state, so the track's state IS the panel's. (The dropdown layer mounts the
// panel under the key `content`: a lookup by name finds nothing.) A switch
// also drops a pending revoke confirmation — it belongs to the view that asked.
export function inviteDropdownMode(value) {
  const track = this && this.parent
  const s = track && track.state
  if (!s || typeof s.update !== 'function' || (value !== 'email' && value !== 'link')) return
  if (s.mode === value) return
  s.update({ mode: value, revokeAsk: '' })
}

// ── Active-link rows (components/InviteDropdown.js) ──────────────────────
// `this` is an element inside the shared MagicLinkInInvite section; `s` a
// link row's state { inviteId, role, endsAt, busy }.

// The panel's own state: the link section's state parent. (The dropdown layer
// mounts the panel under the key `content`, so it is not found by name.)
export function inviteLinkPanelState() {
  // From inside the section `lookup` finds it (it walks UP); from the panel
  // itself the section is a child.
  const host = this.lookup('MagicLinkInInvite') || this.MagicLinkInInvite
  return (host && host.state && host.state.parent) || {}
}

// The row's role, translated (the shared section's own helper).
export function inviteLinkRowRole(s) {
  const host = this.lookup('MagicLinkInInvite')
  return host ? host.scope.magicLinkRoleText(this, s, s.role) : (s && s.role) || ''
}

// The row's end date, short, in the session language.
export function inviteLinkRowDate(s) {
  const host = this.lookup('MagicLinkInInvite')
  return host && s ? host.scope.magicLinkDate(this, s.endsAt) : ''
}

// Is this row asking "Revoke this link?"
export function inviteLinkRowAsking(s) {
  return !!(s && s.inviteId) && this.call('inviteLinkPanelState').revokeAsk === s.inviteId
}

// ── The two-step revoke (components/InviteDropdown.js) ───────────────────
// First press: the row asks "Revoke this link?". The confirm "Revoke" does
// NOT appear where the first one was — that spot (the row's last button) is
// "Keep", and focus moves there — so a double click or a second fast Enter
// lands on the safe choice. Both answers also ignore activations for
// INVITE_REVOKE_GUARD_MS after the swap.
const INVITE_REVOKE_GUARD_MS = 300

// The row element of an open link, from the link section (tree access, by the
// row's data key — never a DOM query).
export function inviteLinkRowElement(inviteId) {
  const section = this.lookup('MagicLinkInInvite') || this.MagicLinkInInvite
  const list = section && section.ActiveLinks && section.ActiveLinks.LinkList
  return (list && inviteId && list['magicLink_' + inviteId]) || null
}

// Focus a control of the panel once the state change has rendered.
export function inviteLinkFocus(target) {
  const node = target && target.node
  if (!node || typeof node.focus !== 'function') return
  node.focus()
  setTimeout(() => {
    if (node.isConnected && typeof node.focus === 'function') node.focus()
  }, 0)
}

// Is an answer to the confirm still inside the guard window?
export function inviteLinkGuarded() {
  const panel = this.call('inviteLinkPanelState')
  return Date.now() - (Number(panel.revokeAskAt) || 0) < INVITE_REVOKE_GUARD_MS
}

// First press on a row's "Revoke": ask, and put focus on "Keep".
export function inviteLinkAsk(s) {
  const panel = this.call('inviteLinkPanelState')
  if (!panel.update || !s || s.busy) return
  panel.update({ revokeAsk: s.inviteId, revokeAskAt: Date.now() })
  const row = this.call('inviteLinkRowElement', s.inviteId)
  if (row) this.call('inviteLinkFocus', row.AppCapsuleBtnGhost_keep)
}

// "Keep" (or Escape): close the question, focus the row's "Revoke".
export function inviteLinkKeep(inviteId) {
  const panel = this.call('inviteLinkPanelState')
  if (!panel.update || !panel.revokeAsk) return
  const id = inviteId || panel.revokeAsk
  panel.update({ revokeAsk: '', revokeAskAt: 0 })
  const row = this.call('inviteLinkRowElement', id)
  if (row) this.call('inviteLinkFocus', row.AppCapsuleBtnGhost_revoke)
}

// The confirm: revoke through the shared section, then focus stays in the
// panel — on "Create and copy link" when the row is gone, on the row's
// "Revoke" when the server refused.
export function inviteLinkConfirm(s) {
  const panel = this.call('inviteLinkPanelState')
  const host = this.lookup('MagicLinkInInvite')
  if (!panel.update || !host || !s || s.busy) return null
  const id = s.inviteId
  panel.update({ revokeAsk: '', revokeAskAt: 0 })
  return Promise.resolve(host.scope.magicLinkRevoke(host, host.state, id, s)).then(() => {
    const row = this.call('inviteLinkRowElement', id)
    const actions = host.LinkActions
    this.call(
      'inviteLinkFocus',
      row ? row.AppCapsuleBtnGhost_revoke : actions && actions.AppCapsuleBtnPrimary
    )
  })
}
